import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  concatMap,
  Observable,
  of,
  switchMap,
  tap,
  map,
  shareReplay,
} from 'rxjs';

import { OnboardingConsts } from '@shared/constants';
import { OnboardingState, UserDataCache } from '@shared/models';
import { getProjectOnboardingView } from '@shared/utils/onboarding.utils';

import { AuthService } from './auth.service';
import { OnboardingService, UserCacheService } from './http-client';

@Injectable({
  providedIn: 'root',
})
export class OnboardingStateService {
  private authService = inject(AuthService);
  private onboardingService = inject(OnboardingService);
  private userCacheService = inject(UserCacheService);

  private readonly CACHE_VERSION = '1.0';

  private readonly companyRefreshTrigger$ = new BehaviorSubject<void>(undefined);
  private readonly projectRefreshTrigger$ = new BehaviorSubject<void>(undefined);
  private readonly projectStates = new Map<string, Observable<OnboardingState>>();
  private readonly projectRequiredSectionDismissed = new Map<string, Observable<boolean>>();

  readonly companyState$: Observable<OnboardingState> = combineLatest([
    this.authService.user$.pipe(filter((user) => !!user?.companyId)),
    this.companyRefreshTrigger$,
  ]).pipe(
    switchMap(([user]) =>
      this.onboardingService.getCompanyOnboardingState(user!.companyId!, OnboardingConsts.CompanyOnboarding)
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  getProjectState$(projectId: string): Observable<OnboardingState> {
    let state = this.projectStates.get(projectId);
    if (!state) {
      state = this.projectRefreshTrigger$.pipe(
        switchMap(() =>
          this.onboardingService.getProjectOnboardingState(projectId, OnboardingConsts.ProjectOnboarding)
        ),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.projectStates.set(projectId, state);
    }

    return state;
  }

  getProjectRequiredSectionDismissed$(projectId: string): Observable<boolean> {
    let dismissed = this.projectRequiredSectionDismissed.get(projectId);
    if (!dismissed) {
      dismissed = this.projectRefreshTrigger$.pipe(
        switchMap(() =>
          this.userCacheService
            .get(
              '',
              OnboardingConsts.RequiredToStart,
              getProjectOnboardingView(projectId),
              OnboardingConsts.ViewDismissed
            )
            .pipe(
              map((cache) => !!(cache?.value && cache.version === this.CACHE_VERSION && JSON.parse(cache.value))),
              catchError(() => of(true))
            )
        ),
        shareReplay({ bufferSize: 1, refCount: true })
      );
      this.projectRequiredSectionDismissed.set(projectId, dismissed);
    }

    return dismissed;
  }

  toggleStepDone(view: string, stepKey: string, isDone: boolean): Observable<UserDataCache> {
    const model: UserDataCache = {
      entityId: stepKey,
      type: OnboardingConsts.StepDone,
      version: this.CACHE_VERSION,
      view: view,
      value: JSON.stringify(isDone),
    };

    return this.userCacheService.add('', model).pipe(
      concatMap((cache) => (isDone ? of(cache) : this.setProgressPopupDismissed(view, false))),
      tap(() => this.refresh(view))
    );
  }

  isProgressPopupDismissed$(view: string): Observable<boolean> {
    const refreshTrigger$: Observable<void> = this.getRefreshTrigger$(view) ?? of(undefined);

    return refreshTrigger$.pipe(
      switchMap(() =>
        this.userCacheService
          .get('', OnboardingConsts.RequiredToStartProgressPopup, view, OnboardingConsts.ViewDismissed)
          .pipe(
            map((cache) => !!(cache?.value && cache.version === this.CACHE_VERSION && JSON.parse(cache.value))),
            catchError(() => of(false))
          )
      )
    );
  }

  dismissProgressPopup(view: string): Observable<UserDataCache> {
    return this.setProgressPopupDismissed(view, true);
  }

  refresh(view: string): void {
    this.getRefreshTrigger$(view)?.next();
  }

  private setProgressPopupDismissed(view: string, isDismissed: boolean): Observable<UserDataCache> {
    const model: UserDataCache = {
      entityId: OnboardingConsts.RequiredToStartProgressPopup,
      type: OnboardingConsts.ViewDismissed,
      version: this.CACHE_VERSION,
      view: view,
      value: JSON.stringify(isDismissed),
    };

    return this.userCacheService.add('', model);
  }

  private getRefreshTrigger$(view: string): BehaviorSubject<void> | null {
    if (view === OnboardingConsts.CompanyOnboarding) {
      return this.companyRefreshTrigger$;
    }

    if (view === OnboardingConsts.ProjectOnboarding || view.startsWith(`${OnboardingConsts.ProjectOnboarding}:`)) {
      return this.projectRefreshTrigger$;
    }

    return null;
  }
}
